import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLinkPreview } from '@/hooks/useLinkPreview';

/**
 * Lightweight in-message embed detection — finds URLs in a message
 * body, classifies each (image / video / YouTube / generic link), and
 * renders a small preview row underneath the text. Generic links unfurl
 * through our server endpoint (`api.unfurl`) — the OG-scrape proxy + cache
 * that this file's TODO used to wait on now exists (SSRF-guarded), so a
 * pasted article/site link becomes a rich read-later card.
 */
export type DetectedEmbed =
  | { kind: 'image'; url: string }
  | { kind: 'video'; url: string }
  | { kind: 'youtube'; id: string; url: string }
  | { kind: 'link'; url: string };

const URL_RE = /https?:\/\/[^\s<>"]+/gi;
const IMG_EXT = /\.(?:png|jpe?g|webp|gif|avif|bmp|svg)(?:\?[^\s]*)?$/i;
const VIDEO_EXT = /\.(?:mp4|webm|mov|m4v)(?:\?[^\s]*)?$/i;

/**
 * Strip trailing punctuation users often type after a pasted URL
 * (`…см. https://x.com/foo.` → drop the trailing dot).
 */
function trimTrailingPunct(url: string): string {
  return url.replace(/[.,;:!?)\]}'"»]+$/, '');
}

/** YouTube video ID from any common URL shape, or null. */
function parseYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1);
      return /^[A-Za-z0-9_-]{6,}$/.test(id) ? id : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      if (u.pathname === '/watch') {
        const id = u.searchParams.get('v');
        return id && /^[A-Za-z0-9_-]{6,}$/.test(id) ? id : null;
      }
      if (u.pathname.startsWith('/shorts/') || u.pathname.startsWith('/embed/')) {
        const id = u.pathname.split('/')[2] ?? '';
        return /^[A-Za-z0-9_-]{6,}$/.test(id) ? id : null;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Walk the message body, classify each URL we find. Caps the result at
 * 3 so a comment with 12 links can't bloat the channel scroll.
 */
export function detectEmbeds(content: string): DetectedEmbed[] {
  const matches = content.match(URL_RE);
  if (!matches) return [];
  const out: DetectedEmbed[] = [];
  const seen = new Set<string>();
  for (const raw of matches) {
    const url = trimTrailingPunct(raw);
    if (seen.has(url)) continue;
    seen.add(url);
    const yt = parseYouTubeId(url);
    if (yt) {
      out.push({ kind: 'youtube', id: yt, url });
    } else if (IMG_EXT.test(url)) {
      out.push({ kind: 'image', url });
    } else if (VIDEO_EXT.test(url)) {
      out.push({ kind: 'video', url });
    } else {
      // Anything else → a generic link to unfurl (renders nothing if the
      // server can't fetch a card, so a bare link just stays linkified text).
      out.push({ kind: 'link', url });
    }
    if (out.length >= 3) break;
  }
  return out;
}

export function MessageEmbeds(props: { items: DetectedEmbed[]; channelId?: string }) {
  return (
    <div className="flex flex-col gap-2">
      {props.items.map((item) => {
        if (item.kind === 'image') {
          return (
            <a
              key={item.url}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-[420px] overflow-hidden rounded-xl transition-opacity hover:opacity-90"
            >
              <img
                src={item.url}
                alt=""
                loading="lazy"
                className="block max-h-[320px] w-full object-cover"
              />
            </a>
          );
        }
        if (item.kind === 'video') {
          return (
            <video
              key={item.url}
              src={item.url}
              controls
              preload="metadata"
              className="block max-h-[320px] max-w-[420px] rounded-xl"
            />
          );
        }
        if (item.kind === 'link') {
          return <LinkEmbed key={item.url} url={item.url} channelId={props.channelId} />;
        }
        // YouTube — lazy thumbnail click-to-load. We don't auto-mount
        // the iframe on every render: it pulls third-party JS and is
        // heavy in a long channel. Instead the thumbnail is a button;
        // clicking swaps to the embed iframe. Cheap + privacy-friendly.
        return <YouTubeEmbed key={item.url} id={item.id} url={item.url} />;
      })}
    </div>
  );
}

/**
 * Read-later card for a generic link: title, site, description and thumbnail
 * fetched (and cached) by the server's unfurl endpoint. Renders nothing while
 * loading or when the link can't be unfurled — the URL stays clickable text.
 */
function LinkEmbed(props: { url: string; channelId?: string }) {
  const preview = useLinkPreview(props.url, props.channelId);
  if (!preview || (!preview.title && !preview.imageUrl)) return null;
  return (
    <a
      href={props.url}
      target="_blank"
      rel="noopener noreferrer"
      className="studio-link-preview flex max-w-[420px] flex-col overflow-hidden rounded-xl bg-surface-secondary transition-colors"
    >
      {preview.imageUrl && <img src={preview.imageUrl} alt="" loading="lazy" className="aspect-[16/9] w-full object-cover" />}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 px-3 py-2.5">
        {preview.siteName && (
          <span className="truncate text-xs font-medium text-accent">
            {preview.siteName}
          </span>
        )}
        {preview.title && (
          <span className="line-clamp-2 text-sm font-semibold text-foreground">{preview.title}</span>
        )}
        {preview.description && (
          <span className="line-clamp-2 text-xs text-ink-dim">{preview.description}</span>
        )}
      </span>

    </a>
  );
}

function YouTubeEmbed(props: { id: string; url: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const thumb = `https://i.ytimg.com/vi/${props.id}/hqdefault.jpg`;
  if (open) {
    return (
      <div className="aspect-video w-full max-w-[480px] overflow-hidden rounded-xl">
        <iframe
          src={`https://www.youtube.com/embed/${props.id}?autoplay=1&rel=0`}
          title={t('messages.playVideo')}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="h-full w-full"
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label={t('messages.playVideo')}
      className="group/yt relative block w-full max-w-[480px] overflow-hidden rounded-xl transition-shadow hover:ring-1 hover:ring-accent/40"
    >
      <img
        src={thumb}
        alt=""
        loading="lazy"
        className="aspect-video w-full object-cover transition-transform duration-300 group-hover/yt:scale-[1.02]"
      />
      <span
        aria-hidden
        className="absolute inset-0 grid place-items-center bg-black/30 transition-colors group-hover/yt:bg-black/40"
      >
        <span className="grid h-12 w-16 place-items-center rounded-lg bg-[#ff0000] text-white transition-transform group-hover/yt:scale-110">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </span>
      <span className="absolute bottom-1.5 left-2 rounded bg-black/70 px-1.5 py-0.5 font-mono text-xs text-white">
        YouTube
      </span>
    </button>
  );
}
